/**
 * @file customer-profile.tsx
 * @module app/modules/customers/customer-profile
 * @description Customer profile page with dashboard, business details, orders, and refunds tabs.
 * Supports profile editing, address updates, and customer-related actions in UTrade Admin.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import defaultImg from '../../../_admin/assets/media/product/default.svg';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import TabsOrders from './tabs/tabs-orders';
import EditBusinessDetails from './tabs/edit-business-details';
import ViewBusinessDetails from './tabs/view-business-details';
import Email from '../../../_admin/assets/media/svg_uTrade/email.svg';
import PhoneCall from '../../../_admin/assets/media/svg_uTrade/phone-call.svg';
import { isValidTin, tinInput } from './customer-tin';
import APICallService from '../../../api/apiCallService';
import { buyer, customerService } from '../../../api/apiEndPoints';
import Loader from '../../../Global/loader';
import { IGetCustomerProfile } from '../../../types/responseIndex';
import TabCustomerDashboard from './tabs/tab-customer-dashboard';
import SummaryInfoCard from '../_common/components/summary-info-card';
import ThreeDots from '../../../_admin/assets/media/svg_uTrade/three-dot.svg';
import TabRefunds from './tabs/tabs-refunds';
import Method from '../../../utils/methods';
import { customerJSON } from '../../../api/apiJSON/customer';
import { fileValidation } from '../../../Global/fileValidation';
import {
  AllCustomers,
  Edit,
  Manager,
  ServiceMember,
} from '../../../utils/constants';
import { searchGoogleAddressUsingLatLong } from '../../../utils/googleAddress';
import edit from '../../../_admin/assets/media/edit-round-large.png';
import { success } from '../../../Global/toast';
import DeleteModal from '../../modals/delete';
import { listCustomerProfile } from '../../../utils/storeString';
import { getKey, setKey } from '../../../Global/history';
import { useAuth } from '../auth';
import { warehouse as warehouseStoreString } from '../../../utils/storeString';
import { useWarehouse } from '../../context/WarehouseContext';

const CustomerProfile = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location: any = useLocation();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editData, setEditData] = useState<IGetCustomerProfile | undefined>(
    undefined
  );
  const [profileData, setProfileData] = useState<
    IGetCustomerProfile | undefined
  >(undefined);
  const [initialCustomerData, setInitialCustomerData] = useState<any>({
    name: '',
    email: '',
    address: '',
    vatNumber: '',
    tinNumber: '',
    image: '',
    businessName: '',
    types: {},
    phoneNumber: '',
    phoneCountry: '+255',
    lat: '1',
    district: '',
    districtName: '',
    lng: '1',
    deliveryAddresses: [],
    isPharmacyVendor: false,
  });
  const [customerData, setCustomerData] = useState<{
    name: string;
    email: string;
    businessName: string;
    district: string;
    districtName: string;
    address: string;
    vatNumber: string;
    tinNumber: any;
    image: any;
    imageReader: any;
    types: any;
    phoneNumber: string;
    phoneCountry: string;
    lat: string;
    lng: string;
    deliveryAddresses: any;
    analytics: any;
    isPharmacyVendor?: boolean;
  }>({
    name: '',
    email: '',
    address: '',
    vatNumber: '',
    tinNumber: '',
    image: '',
    businessName: '',
    imageReader: {},
    types: {},
    phoneNumber: '',
    phoneCountry: '+255',
    lat: '1',
    district: '',
    districtName: '',
    lng: '1',
    deliveryAddresses: [],
    analytics: [],
    isPharmacyVendor: false,
  });
  const [customerValidation, setCustomerValidation] = useState<{
    name: boolean;
    businessName: boolean;
    address: boolean;
    types: boolean;
    phoneNumber: boolean;
    tinNumber: boolean;
  }>({
    name: false,
    address: false,
    businessName: false,
    types: false,
    phoneNumber: false,
    tinNumber: false,
  });
  const [editCustomerProfile, setEditCustomerProfile] = useState(false);
  const [tab, setTab] = useState([
    { name: 'Dashboard', content: <TabCustomerDashboard />, value: '1' },
    { name: 'Business details', content: <ViewBusinessDetails />, value: '2' },
    { name: 'Orders', content: <TabsOrders />, value: '3' },
    { name: 'Refunds', content: <TabRefunds />, value: '4' },
  ]);
  const [districts, setDistricts] = useState([]);

  const [currentTab, setCurrentTab] = useState<any>(() => {
    const storedTabValue = getKey(listCustomerProfile?.currentTab);
    const matchingTab: any = tab?.find((tab) => tab?.value == storedTabValue);
    return matchingTab || tab[1];
  });

  const createTabs = () => {
    const localTab = tab;
    let current = currentTab;
    const allTabs = localTab?.map((tab) => {
      const tabEventName = tab.name.toLowerCase().replace(/\s+/g, '_');
      return (
        <li className="nav-item">
          <Link
            to={'/'}
            className={
              current?.value === tab?.value
                ? 'nav-link active text-active-dark'
                : 'nav-link text-hover-dark'
            }
            data-bs-toggle="tab"
            data-analytics-event={`customers_customer_profile_${tabEventName}_clicked`}
            onClick={() => handleSelectTab(tab)}
          >
            {tab?.name}
          </Link>
        </li>
      );
    });
    return allTabs;
  };
  const fetchDistrict = async () => {
    let params: any = {};
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      customerService.listDistrict,
      params,
      '',
      '',
      '',
      '',
      AllCustomers,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    let data: any = [...districts];
    setDistricts(response?.records);
  };
  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchProfileData();
      fetchDistrict();
      setLoading(false);
    })();
  }, [selectedWarehouse?.value]);
  useEffect(() => {
    if (editCustomerProfile) {
      const businessDetailsTab = tab?.find((t) => t?.value === '2');
      if (businessDetailsTab) {
        setCurrentTab({
          ...businessDetailsTab,
          content: getTabContent('2'),
        });
        setKey(listCustomerProfile?.currentTab, '2', false);
      }
    }
  }, [editCustomerProfile]);

  const fetchProfileData = async () => {
    const customerId = location.state
      ? location.state
      : localStorage.getItem('CustomerId');
    let apiService = new APICallService(
      buyer.details,
      customerId,
      '',
      '',
      '',
      '',
      AllCustomers,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    if (response) {
      setProfileData(JSON.parse(JSON.stringify(response)));
      setEditData(JSON.parse(JSON.stringify(response)));
      // setCurrentTab({
      //   name: 'Dashboard',
      //   content: <TabCustomerDashboard customerId={location.state} />,
      //   value: '1',
      // });
      setCustomerData({
        name: response?.user?.name || '',
        email: response?.user?.email || '',
        address: response?.user?.business?.address || '',
        vatNumber: response?.user?.business?.vatNumber || '',
        tinNumber: response?.user?.business?.tinNumber || '',
        image: response?.user?.business?.image || '',
        businessName: response?.user?.business?.name || '',
        imageReader: {},
        types: response?.user?.business?.types?.length
          ? response?.user?.business?.types[0]?.reference
          : '',
        phoneNumber: response?.user?.phone || '',
        phoneCountry: '+255',
        lat: response?.user?.business?.lat || '1',
        district: response?.user?.business?.district || '',
        districtName: response?.user?.business?.districtName || '',
        lng: response?.user?.business?.lng || '1',
        deliveryAddresses: response?.user?.deliveryAddresses || [],
        analytics: response?.user?.analytics || [],
        isPharmacyVendor: response?.user?.isPharmacyVendor || false,
      });
      setInitialCustomerData({
        name: response?.user?.name || '',
        email: response?.user?.email || '',
        address: response?.user?.business?.address || '',
        vatNumber: response?.user?.business?.vatNumber || '',
        tinNumber: response?.user?.business?.tinNumber || '',
        image: response?.user?.business?.image || '',
        businessName: response?.user?.business?.name || '',
        imageReader: {},
        types: response?.user?.business?.types?.length
          ? response?.user?.business?.types[0]?.reference
          : '',
        phoneNumber: response?.user?.phone || '',
        phoneCountry: '+255',
        lat: response?.user?.business?.lat || '1',
        district: response?.user?.business?.district || '',
        districtName: response?.user?.business?.districtName || '',
        lng: response?.user?.business?.lng || '1',
        deliveryAddresses: response?.user?.deliveryAddresses || [],
        isPharmacyVendor: response?.user?.isPharmacyVendor || false,
      });
      localStorage.setItem('customer', JSON.stringify(response));
      // setCurrentTab({
      //   name: 'Business details',
      //   content: editCustomerProfile ? (
      //     <EditBusinessDetails editData={editData} />
      //   ) : (
      //     <ViewBusinessDetails profileData={profileData} />
      //   ),
      //   value: '2',
      // });
    }
  };
  const handleBasicDetailsChange = (name: string, value: string) => {
    let temp: any = { ...customerData };
    let tempValidation: any = { ...customerValidation };
    temp[name] = value?.trimStart();
    if (name === 'tinNumber') {
      temp[name] = tinInput(value);
      tempValidation[name] = !isValidTin(temp[name]);
    } else if (Object?.keys(tempValidation)?.includes(name)) {
      tempValidation[name] = false;
      if (!value?.length) tempValidation[name] = true;
    }
    setCustomerData(temp);
    setCustomerValidation(tempValidation);
  };
  const handlePharmacyToggle = (checked: boolean) => {
    let temp: any = { ...customerData };
    temp.isPharmacyVendor = checked;
    setCustomerData(temp);
  };
  const handleBusinessTypesSelect = (id: any) => {
    let temp: any = { ...customerData };
    let tempValidation: any = { ...customerValidation };
    temp.types = id;
    tempValidation.types = false;
    setCustomerData(temp);
    setCustomerValidation(tempValidation);
  };

  const handleDistrictSelect = (event: any) => {
    let temp: any = { ...customerData };
    temp.district = event._id;
    temp.districtName = event.label;
    setCustomerData(temp);
  };

  const handleImageChange = (event: any) => {
    const selectedFiles = event.target.files;
    let temp = { ...customerData };
    if (!selectedFiles) return;
    else {
      if (fileValidation(selectedFiles?.[0])) {
        temp.image = URL.createObjectURL(selectedFiles?.[0]);
        temp.imageReader = selectedFiles?.[0];
      }
    }
    setCustomerData(temp);
  };
  const handleAddCustomer = async () => {
    let temp = { ...customerData };
    let validateTemp = { ...customerValidation };
    if (temp?.name === '') {
      validateTemp.name = true;
    }
    if (temp?.businessName === '') {
      validateTemp.businessName = true;
    }
    if (temp?.phoneNumber === '') {
      validateTemp.phoneNumber = true;
    }
    // if (temp.email === '') {
    //   validateTemp.email = true;
    // }
    if (temp?.address === '') {
      validateTemp.address = true;
    }
    if (temp?.types === '') {
      validateTemp.types = true;
    }
    if (!isValidTin(temp?.tinNumber)) {
      validateTemp.tinNumber = true;
    }
    if (Object?.values(validateTemp)?.every((val) => val === false)) {
      setSaveLoading(true);
      let temp = { ...customerData };
      temp.types = temp.types._id;
      let apiService = new APICallService(
        buyer.updateProfile,
        customerJSON.addCustomer(temp),
        {
          id: location.state,
        },
        '',
        '',
        '',
        AllCustomers,
        selectedWarehouse?.value || null
      );
      let response = await apiService.callAPI();
      if (response) {
        // navigate('/customers');
        await fetchProfileData();
        setEditCustomerProfile(false);
        let { types: newTypes, ...restCustomerData }: any = customerData;
        restCustomerData.types = newTypes.title;
        let { types: oldTypes, ...restInitialCustomerData }: any =
          initialCustomerData;
        restInitialCustomerData.types = oldTypes.title;
        const data = Method.compareComplexObjects(
          restInitialCustomerData,
          restCustomerData
        );
        console.log('data', customerData);
        
      }
      setSaveLoading(false);
    }
    setCustomerValidation(validateTemp);
  };
  const handleOnKeyPress = (event: any) => {
    var charCode = event.which ? event.which : event.keyCode;
    if (
      (charCode >= 65 && charCode <= 90) || // A-Z
      (charCode >= 97 && charCode <= 122) || // a-z
      (charCode >= 48 && charCode <= 57) // 0-9
    ) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  };
  const handleAddressChange = async (
    value: string,
    lat: string,
    lng: string
  ) => {
    const googleAddress = await searchGoogleAddressUsingLatLong(lat, lng, {
      searchText: value,
      districts,
    });
    const selectedAddress = googleAddress?.data?.[0];
    const normalizedAddress = value.trimStart();

    setCustomerData((prev: any) => ({
      ...prev,
      wardName: selectedAddress?.wardName || 'NA',
      localityName: selectedAddress?.localityName || '',
      countryName: selectedAddress?.countryName || '',
      districtName: selectedAddress?.districtName || 'UpCountry',
      district: selectedAddress?.district || '651d5fa8317c6e0dcdcd8fa1',
      address: normalizedAddress,
      lat,
      lng,
    }));

    setCustomerValidation((prev: any) => ({
      ...prev,
      district: !selectedAddress?.status,
      address: !normalizedAddress.length,
    }));
  };
  const onProfileChange = async (event: any) => {
    let temp: any = { ...customerData };
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;
    else {
      temp.image = URL.createObjectURL(selectedFiles?.[0]);
      temp.imageReader = selectedFiles?.[0];
    }
    setCustomerData(temp);
  };
  const handleCategorySelect = (id: any) => {
    let temp: any = { ...customerData };
    temp.category = id;
    setCustomerData(temp);
  };
  const getTabContent = (tabValue: any) => {
    switch (tabValue) {
      case '1':
        return (
          <TabCustomerDashboard
            customerId={location.state}
            moduleName={AllCustomers}
          />
        );
      case '2':
        return editCustomerProfile ? (
          <EditBusinessDetails
            editData={editData}
            customerData={customerData}
            customerValidation={customerValidation}
            handleBasicDetailsChange={handleBasicDetailsChange}
            handleBusinessTypesSelect={handleBusinessTypesSelect}
            handleImageChange={handleImageChange}
            handleAddCustomer={handleAddCustomer}
            handleAddressChange={handleAddressChange}
            handleOnKeyPress={handleOnKeyPress}
            moduleName={AllCustomers}
            handleCategoryChange={handleCategorySelect}
            districts={districts}
            handleDistrictSelect={handleDistrictSelect}
            handlePharmacyToggle={handlePharmacyToggle}
          />
        ) : (
          <ViewBusinessDetails
            profileData={profileData}
            moduleName={AllCustomers}
            onAddressSaved={async () => {
              console.log('here');
              await fetchProfileData();
            }}
          />
        );
      case '3':
        return (
          <TabsOrders customerId={location?.state} moduleName={AllCustomers} />
        );
      case '4':
        return (
          <TabRefunds customerId={location?.state} moduleName={AllCustomers} />
        );
      default:
        return null;
    }
  };
  const currentTabContent = useMemo(
    () => getTabContent(currentTab.value),
    [
      currentTab?.value,
      editCustomerProfile,
      editData,
      customerData,
      customerValidation,
      profileData,
    ]
  );
  const handleSelectTab = (tab: any) => {
    setEditCustomerProfile(false);
    setCurrentTab({
      ...tab,
      content: getTabContent(tab?.value),
    });
    setKey(listCustomerProfile.currentTab, tab.value, false);
  };
  console.log('profileData', profileData);
  return (
    <>
      <div className="p-5">
        <Row className="mb-7 align-items-center">
          <Col md>
            <h1 className="fs-22 fw-bolder mb-md-0 mb-3">Customer profile</h1>
          </Col>
          {!loading ? (
            <Col md="auto">
              {editCustomerProfile ? (
                <div className="d-flex flex-row">
                  {saveLoading ? null : (
                    <>
                      {' '}
                      <Button
                        variant="light-primary"
                        className="uw-btn me-3"
                        data-analytics-event="customers_customer_profile_cancel_clicked"
                        onClick={() => {
                          setEditCustomerProfile(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  <Button
                    variant="primary"
                    className="uw-btn uw-btn-primary me-3"
                    data-analytics-event="customers_customer_profile_save_changes_clicked"
                    onClick={() => {
                      handleAddCustomer();
                    }}
                    disabled={saveLoading}
                  >
                    {!saveLoading && (
                      <span className="indicator-label fs-16 fw-bold">
                        Save changes
                      </span>
                    )}
                    {saveLoading && (
                      <span
                        className="indicator-progress fs-16 fw-bold"
                        style={{ display: 'block' }}
                      >
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="d-flex flex-row">
                  {Method.hasPermission(AllCustomers, Edit, currentUser) ? (
                    <Button
                      variant="light-primary"
                      className="uw-btn me-3"
                      data-analytics-event="customers_customer_profile_edit_profile_clicked"
                      onClick={() => {
                        setEditCustomerProfile(true);
                      }}
                    >
                      Edit profile details
                    </Button>
                  ) : null}
                </div>
              )}
            </Col>
          ) : null}
        </Row>
        {loading ? (
          <div className="d-flex justify-content-center">
            <Loader loading={loading} />
          </div>
        ) : (
          <>
            <Card className="border border-custom-color mb-7">
              <Card.Body className="pt-9 pb-0">
                <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
                  <div className="me-9">
                    <div className="symbol symbol-200px symbol-fixed border border-r10px position-relative">
                      <div className="image-input d-flex flex-center rounded w-lg-200px h-lg-200px">
                        <div
                          className="image-input-wrapper shadow-none bgi-cover bgi-position-center border-r10px w-100 h-100"
                          style={{
                            backgroundImage: `url(${
                              editCustomerProfile
                                ? customerData?.image
                                  ? customerData?.image
                                  : defaultImg
                                : profileData?.user?.business?.image
                                ? profileData?.user?.business?.image
                                : defaultImg
                            })`,
                          }}
                        ></div>
                        {editCustomerProfile ? (
                          <label
                            className="btn btn-icon btn-circle btn-active-color-primary w-50px h-50px top-100 bottom-0"
                            data-kt-image-input-action="change"
                            title=""
                          >
                            <img src={edit} height={33} width={33} alt="" />
                            <input
                              type="file"
                              name="avatar"
                              accept=".png, .jpg, .jpeg"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => onProfileChange(e)}
                            />
                            <input type="hidden" name="avatar_remove" />
                          </label>
                        ) : (
                          <React.Fragment></React.Fragment>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row align-items-center flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column mb-2">
                          <h3 className="text-black fs-22 fw-bolder">
                            {profileData?.user?.business?.name}
                          </h3>
                          <div className="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2">
                            <div className="d-flex align-items-center text-black fs-16 fw-500 me-5 mb-2">
                              <span className="svg-icon svg-icon-4 me-2">
                                <img src={PhoneCall} alt="Phone number" />
                              </span>{' '}
                              {profileData?.user?.phoneCountry}{' '}
                              {profileData?.user?.phone}
                            </div>
                            {profileData?.user?.email ? (
                              <div className="d-flex align-items-center text-black fs-16 fw-500 mb-2">
                                <span className="svg-icon svg-icon-4 me-2">
                                  <img src={Email} alt="Email" />
                                </span>
                                {profileData?.user?.email}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <Col lg={12}>
                      <div className="d-flex flex-wrap flex-stack">
                        <div className="d-flex flex-column flex-grow-1 pe-8">
                          <Row className="g-5">
                            <SummaryInfoCard
                              colKey="total-orders"
                              bgClassName="bg-light"
                              value={
                                currentUser?.role.some(
                                  (obj: any) => obj.type === Manager
                                ) ||
                                currentUser?.role.some(
                                  (obj: any) => obj.type === ServiceMember
                                )
                                  ? customerData?.analytics
                                      ?.map((val: any) => val.totalOrders)
                                      .reduce(
                                        (a: number, b: number) => a + b,
                                        0
                                      )
                                      .toString()
                                      .padStart(2, '0')
                                  : customerData?.analytics[0]?.totalOrders
                                  ? customerData?.analytics[0]?.totalOrders
                                      .toString()
                                      .padStart(2, '0')
                                  : '00'
                              }
                              label="Total orders"
                              colProps={{ xl: 3, md: 6, xs: 12 }}
                            />
                            <SummaryInfoCard
                              colKey="average-order"
                              bgClassName="bg-light"
                              value={`${
                                customerData?.analytics[0]?.orderFrequency ?? 0
                              } days`}
                              label="Average Order"
                              colProps={{ xl: 3, md: 6, xs: 12 }}
                            />
                            <SummaryInfoCard
                              colKey="average-order-value"
                              bgClassName="bg-light"
                              value={`TSh ${
                                Method.getGeneralizedAmount(
                                  customerData?.analytics[0]?.averageOrderValue
                                ) ?? 0
                              }`}
                              label="Average Order value"
                              colProps={{ xl: 3, md: 6, xs: 12 }}
                            />
                            <SummaryInfoCard
                              colKey="total-order-value"
                              bgClassName="bg-light"
                              value={`TSh ${
                                Method.getGeneralizedAmount(
                                  customerData?.analytics[0]?.totalOrderValue
                                ) ?? 0
                              }`}
                              label="Total Order value"
                              colProps={{ xl: 3, md: 6, xs: 12 }}
                            />
                            <SummaryInfoCard
                              colKey="assigned-member"
                              bgClassName="bg-light"
                              value={
                                profileData?.user.assignedTo
                                  ? profileData?.user.assignedTo.name
                                  : 'NA'
                              }
                              label="Assigned member"
                              colProps={{ xl: 3, md: 6, xs: 12 }}
                            />
                          </Row>
                        </div>
                      </div>
                    </Col>
                  </div>
                </div>
              </Card.Body>
              <Row className="align-items-center px-lg-9 px-6">
                <Col sm>
                  <div className="d-flex h-70px">
                    <ul className="nav nav-stretch nav-line-tabs nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-transparent fs-18 fw-600">
                      {createTabs()}
                    </ul>
                  </div>
                </Col>
              </Row>
            </Card>
            <div className="tab-content" id="myTabContent">
              <div className="tab-pane fade show active">
                <>{currentTabContent}</>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
export default CustomerProfile;
